import { useState, useEffect } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import Breadcrumbs from '@/pages/Blog/components/Breadcrumbs';
import JsonLd, { breadcrumbSchema } from '@/components/base/JsonLd';
import useSeo, { SITE_URL } from '@/hooks/useSeo';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function UnsubscribePage() {
  useSeo({
    title: 'Unsubscribe | TNT Coach AI',
    description: 'Manage your TNT Coach AI email preferences. Enter your email address to unsubscribe from our email notifications at any time.',
    canonical: `${SITE_URL}/unsubscribe`,
    url: `${SITE_URL}/unsubscribe`,
    noindex: true,
  });

  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('company_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setErrorMessage('');

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'company_alt') {
          urlEncoded.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/dak22ovh3l9b18n2bp10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;

      if (!response.ok || (parsed.code && parsed.code !== 'OK') || /spam/i.test(serverMessage)) {
        throw new Error(serverMessage || 'Unsubscribe request failed');
      }

      setFormStatus('success');
      form.reset();
    } catch (err) {
      setFormStatus('error');
      setErrorMessage(
        err instanceof Error && err.message && !/spam/i.test(err.message)
          ? err.message
          : 'Something went wrong. Please try again or email us at support@tntcoach.ai.'
      );
    }
  };

  return (
    <div className="min-h-screen bg-midnight">
      <JsonLd
        data={breadcrumbSchema([
          { name: 'Home', url: `${SITE_URL}/` },
          { name: 'Unsubscribe', url: `${SITE_URL}/unsubscribe` },
        ])}
      />
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-32 pb-12">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Unsubscribe' }]} />
              <div className="text-center">
                <p className="text-champagne-gold text-sm font-medium tracking-wider uppercase mb-3">Email Preferences</p>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-silver-grey mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Unsubscribe
                </h1>
                <p className="text-silver-grey/60 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
                  We are sorry to see you go. Enter your email address below and we will remove you from our email notifications.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="border-t border-royal-navy/30" />
        </div>

        {/* Unsubscribe Form */}
        <section className="py-12 md:py-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-xl mx-auto">
              <div className="rounded-lg bg-royal-navy/20 border border-royal-navy/30 p-8 md:p-10">
                <form
                  data-readdy-form
                  id="unsubscribe-form"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  {/* Honeypot */}
                  <div className="unsubscribe-company-alt-wrapper" aria-hidden="true">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-silver-grey text-sm font-medium mb-2">
                      Email Address <span className="text-champagne-gold">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-lg bg-warm-white border border-royal-navy/20 text-midnight placeholder:text-taupe-grey focus:outline-none focus:border-champagne-gold/60 focus:ring-1 focus:ring-champagne-gold/30 transition-all duration-300 text-sm"
                    />
                    <p className="text-silver-grey/40 text-xs mt-1.5">
                      We will only use this address to process your unsubscribe request.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full gold-gradient text-midnight font-semibold text-sm hover:opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Unsubscribe Me
                        <i className="ri-mail-close-line" />
                      </>
                    )}
                  </button>

                  {/* Status Messages */}
                  {formStatus === 'success' && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-champagne-gold/10 border border-champagne-gold/30 animate-fade-in">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-line text-champagne-gold" />
                      </div>
                      <div>
                        <p className="text-champagne-gold text-sm font-medium">You are unsubscribed</p>
                        <p className="text-silver-grey/70 text-sm mt-1">
                          You have been removed from our email notifications. You can resubscribe anytime if you change your mind.
                        </p>
                      </div>
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30 animate-fade-in">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-red-400" />
                      </div>
                      <div>
                        <p className="text-red-400 text-sm font-medium">Oops, something went wrong</p>
                        <p className="text-silver-grey/70 text-sm mt-1">{errorMessage}</p>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              <p className="text-silver-grey/40 text-xs text-center mt-6">
                Having trouble? Contact us at{' '}
                <a
                  href="mailto:support@tntcoach.ai"
                  className="text-champagne-gold/70 hover:text-champagne-gold transition-colors duration-300"
                >
                  support@tntcoach.ai
                </a>
              </p>
            </div>
          </div>
        </section>

        {/* Back link */}
        <section className="pb-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-xl mx-auto">
              <a
                href="/"
                className="inline-flex items-center gap-2 text-champagne-gold hover:text-champagne-gold/80 transition-colors duration-300 text-sm"
              >
                <i className="ri-arrow-left-line" />
                Back to Home
              </a>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}